import React from 'react';
import { App, Button, Space, Typography, Upload } from 'antd';
import { DeleteOutlined, UploadOutlined } from '@ant-design/icons';

const MAX_BYTES = 2 * 1024 * 1024;
const ACCEPTED = ['image/png', 'image/jpeg'];

type Props = {
  /** Uploaded image as a data URL; empty when the built-in artwork is used. */
  value: string;
  onChange: (dataUrl: string) => void;
  /** Built-in artwork shown (dimmed) when nothing is uploaded. */
  fallbackSrc: string;
  hint: string;
};

/** Upload / preview / remove for one letterhead band (header or footer). PNG or JPG only, since server PDFs accept no other format. */
export const LetterheadImageField: React.FC<Props> = ({ value, onChange, fallbackSrc, hint }) => {
  const { message } = App.useApp();

  const beforeUpload = (file: File) => {
    if (!ACCEPTED.includes(file.type)) {
      message.error('Upload a PNG or JPG image.');
      return Upload.LIST_IGNORE;
    }
    if (file.size > MAX_BYTES) {
      message.error('The image must be 2 MB or smaller.');
      return Upload.LIST_IGNORE;
    }
    const reader = new FileReader();
    reader.onload = (e) => onChange(String(e.target?.result || ''));
    reader.readAsDataURL(file);
    return false;
  };

  return (
    <Space direction="vertical" style={{ width: '100%' }}>
      <div
        style={{
          border: '1px dashed #d9d9d9',
          borderRadius: 6,
          padding: 8,
          background: '#fff',
          minHeight: 48,
          display: 'flex',
          alignItems: 'center',
        }}
      >
        <img
          src={value || fallbackSrc}
          alt=""
          style={{ maxWidth: '100%', maxHeight: 120, objectFit: 'contain', opacity: value ? 1 : 0.45 }}
        />
      </div>
      <Typography.Text type="secondary" style={{ fontSize: 12 }}>
        {value ? hint : `Using the built-in artwork (shown dimmed). ${hint}`}
      </Typography.Text>
      <Space>
        <Upload accept=".png,.jpg,.jpeg" showUploadList={false} beforeUpload={beforeUpload}>
          <Button icon={<UploadOutlined />}>{value ? 'Replace' : 'Upload'}</Button>
        </Upload>
        {value ? (
          <Button icon={<DeleteOutlined />} danger onClick={() => onChange('')}>
            Remove
          </Button>
        ) : null}
      </Space>
    </Space>
  );
};
